<template>
  <div id="total-attendances-report">
    <h3 class="mb-2">Relatórios & Exportações</h3>
    <p class="mb-4 text-gray-600">
      Atendimentos Totais / Volumetria (OKR 24h). Preencha o período, escopo e segmentação. Tenant específico: arquivo na hora (email opcional). Todos os tenants: envio por email.
    </p>

    <vs-alert
      v-if="isAsyncMode"
      active
      class="mb-4"
      color="warning"
      title="Envio por email"
    >
      O relatório pode demorar alguns minutos. Informe o email e você receberá o arquivo quando estiver pronto.
    </vs-alert>
    <vs-alert
      v-else
      active
      class="mb-4"
      color="success"
      title="Arquivo na hora"
    >
      Período curto com tenant específico. O arquivo será gerado e baixado automaticamente. Email opcional.
    </vs-alert>

    <vs-card>
      <div class="vx-row">
        <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-base">
          <label class="block mb-2 font-medium">Data Inicial</label>
          <flat-pickr
            v-model="fromDate"
            v-validate="'required'"
            :config="configDatePicker"
            name="fromDate"
            placeholder="Selecione a data inicial"
            class="w-full"
          />
          <span v-show="errors.has('fromDate')" class="text-danger text-sm">{{ errors.first('fromDate') }}</span>
        </div>
        <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-base">
          <label class="block mb-2 font-medium">Data Final</label>
          <flat-pickr
            v-model="toDate"
            v-validate="'required'"
            :config="toDateConfig"
            name="toDate"
            placeholder="Selecione a data final"
            class="w-full"
            :disabled="!fromDate"
          />
          <span v-show="errors.has('toDate')" class="text-danger text-sm">{{ errors.first('toDate') }}</span>
        </div>
        <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-base">
          <label class="block mb-2 font-medium">Escopo</label>
          <v-select
            v-model="scope"
            v-validate="'required'"
            :options="scopeOptions"
            class="w-full"
            name="scope"
            placeholder="Selecione o escopo"
            @input="onScopeChange"
          />
          <span v-show="errors.has('scope')" class="text-danger text-sm">{{ errors.first('scope') }}</span>
        </div>
        <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-base">
          <label class="block mb-2 font-medium">Segmentação</label>
          <v-select
            v-model="segment"
            v-validate="'required'"
            :options="segmentOptions"
            class="w-full"
            name="segment"
            placeholder="Selecione a segmentação"
          />
          <span v-show="errors.has('segment')" class="text-danger text-sm">{{ errors.first('segment') }}</span>
        </div>
      </div>

      <div class="vx-row flex items-end">
        <div v-if="scope && scope.value === 'specific'" class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-base">
          <label class="block mb-2 font-medium">Tenant (ID ou Nome)</label>
          <vs-input v-model="tenantIdentifier" placeholder="Digite o ID ou nome do tenant" class="w-full" />
          <span v-show="tenantIdentifierError" class="text-danger text-sm">{{ tenantIdentifierError }}</span>
        </div>
        <div class="vx-col w-full sm:w-1/2 lg:w-1/2 mb-base">
          <label class="block mb-2 font-medium">
            Email
            <span v-if="!isAsyncMode" class="text-gray-400">(opcional)</span>
            <span v-else class="text-danger">*</span>
          </label>
          <vs-input
            v-model="email"
            v-validate="'email'"
            name="email"
            placeholder="Digite o email para recebimento do relatório"
            class="w-full"
          />
          <span v-show="errors.has('email')" class="text-danger text-sm">{{ errors.first('email') }}</span>
        </div>
        <div class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-base col-checkbox-resumido">
          <vs-checkbox v-model="estimation" class="checkbox-resumido">
            Relatório resumido (estimativa)
          </vs-checkbox>
        </div>
      </div>

      <div class="vx-row mt-4">
        <div class="vx-col w-full">
          <vs-button color="primary" :disabled="generating" :loading="generating" @click="generateReport">
            Gerar Relatório
          </vs-button>
        </div>
      </div>
    </vs-card>

    <vs-alert v-if="successMessage" active class="mt-6" color="success" title="Sucesso">
      {{ successMessage }}
    </vs-alert>
    <vs-alert v-if="downloadUrl" active class="mt-6" color="success" title="Arquivo pronto">
      <a :href="downloadUrl" target="_blank" rel="noopener">Baixar arquivo</a>
    </vs-alert>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from 'vue-select';
import 'flatpickr/dist/flatpickr.css';
import flatPickr from 'vue-flatpickr-component';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
import tenantRequests from '@/http/requests/tenants/index.js';

const SCOPE_OPTIONS = [
  { label: 'Tenant específico', value: 'specific' },
  { label: 'Todos os Tenants', value: 'all' }
];
const SEGMENT_OPTIONS = [
  { label: 'Apenas Cloud', value: 'cloud' },
  { label: 'Apenas Web', value: 'web' },
  { label: 'Ambos', value: 'both' }
];

export default {
  name: 'TotalAttendancesReport',
  components: { flatPickr, vSelect },
  data() {
    return {
      fromDate: '',
      toDate: '',
      scope: null,
      scopeOptions: SCOPE_OPTIONS,
      tenantIdentifier: '',
      segment: null,
      segmentOptions: SEGMENT_OPTIONS,
      email: '',
      estimation: false,
      generating: false,
      successMessage: '',
      downloadUrl: '',
      tenantIdentifierError: '',
      configDatePicker: {
        enableTime: false,
        dateFormat: 'Y-m-d',
        locale: Portuguese
      }
    };
  },
  computed: {
    ...mapGetters(['token']),
    toDateConfig() {
      return { ...this.configDatePicker, minDate: this.fromDate };
    },
    isAsyncMode() {
      return this.scope && this.scope.value === 'all';
    }
  },
  methods: {
    onScopeChange() {
      if (this.scope && this.scope.value === 'all') {
        this.tenantIdentifier = '';
        this.tenantIdentifierError = '';
      }
    },
    async generateReport() {
      this.tenantIdentifierError = '';
      if (!(await this.$validator.validateAll())) return;

      const scopeValue = (this.scope && this.scope.value) || 'specific';
      if (scopeValue === 'specific') {
        const v = (this.tenantIdentifier && this.tenantIdentifier.trim()) || '';
        if (!v) {
          this.tenantIdentifierError = 'Informe o ID ou nome do tenant.';
          this.$vs.notify({ color: 'danger', title: 'Erro', text: this.tenantIdentifierError });
          return;
        }
      }
      if (this.isAsyncMode && !this.email) {
        this.$vs.notify({ color: 'danger', title: 'Erro', text: 'Informe o email para receber o relatório.' });
        return;
      }

      const payload = {
        fromDate: this.fromDate,
        toDate: this.toDate,
        scope: scopeValue,
        segment: (this.segment && this.segment.value) || 'both',
        email: this.email || null,
        estimation: this.estimation
      };
      payload.tenantIdentifier = payload.scope === 'specific'
        ? (this.tenantIdentifier && this.tenantIdentifier.trim()) || null
        : null;

      this.generating = true;
      this.successMessage = '';
      this.downloadUrl = '';
      try {
        const res = await tenantRequests.generateTotalAttendancesReport(payload, this.token);
        const data = res && res.data;
        if (data && data.downloadUrl) {
          this.downloadUrl = data.downloadUrl;
          this.successMessage = 'Relatório gerado.';
          const a = document.createElement('a');
          a.href = data.downloadUrl;
          a.setAttribute('download', '');
          a.setAttribute('target', '_blank');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else if (data && data.message) {
          this.successMessage = data.message;
        } else {
          this.successMessage = 'Relatório solicitado. Você receberá o arquivo por email.';
        }
        this.$vs.notify({ color: 'success', title: 'Sucesso', text: this.successMessage });
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message)
          || 'Erro ao gerar o relatório. Tente novamente.';
        this.$vs.notify({ color: 'danger', title: 'Erro', text: msg });
      } finally {
        this.generating = false;
      }
    }
  }
};
</script>

<style scoped>
#total-attendances-report .vx-col label { font-size: 0.875rem; }
#total-attendances-report .col-checkbox-resumido { align-self: center; }
#total-attendances-report .checkbox-resumido { margin-top: 18px; }
</style>
